/* istanbul ignore file */
import { useToken } from '@ori/auth';
import type { FC, PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { useWasTooltipShown } from '../../hooks/useWasTooltipShown';
import { ReferralType } from '../../models';
import type { PersonalBeautyStoreContext } from '../../types';
import { useConfiguration } from '../ConfigurationProvider';
import { personalBeautyStoreContext } from './personalBeautyStoreContext';
import { useCookieStoreInfo } from './useCookieStoreInfo';
import { useInviteFriendData } from './useInviteFriendData';
import { usePersonalBeautyStoreData } from './usePersonalBeautyStoreData';
import { usePersonalBeautyStoreUserInfo } from './usePersonalBeautyStoreUserInfo';

export const PersonalBeautyStoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isEnabled, isTooltipEnabled } = useConfiguration();
  const { isAnonymousUser } = useToken();
  const storeInfo = useCookieStoreInfo();
  const pbsData = usePersonalBeautyStoreData();
  const inviteFriendData = useInviteFriendData();
  const [tooltipWasShown, setTooltipWasShown] = useWasTooltipShown();
  const { firstName, lastName } = usePersonalBeautyStoreUserInfo();

  /**
   * Anonymous visitor on referral program market (cookie ecref exists).
   */
  const isAnonymousUserOnReferralMarket = isAnonymousUser && storeInfo.type === ReferralType.ECREF;

  /**
   * Sponsor info (eg. MenuItem) can be displayed to logged in EndCustomers who have sponsor or anonymous visitors to the non-referral program market.
   */
  const canDisplaySponsorInfo = isEnabled && Boolean(pbsData?.beautyStore?.profile) && !isAnonymousUserOnReferralMarket;

  const canDisplayTooltip = isEnabled && !tooltipWasShown && Boolean(firstName ?? lastName) && isTooltipEnabled;

  const value = useMemo(
    (): PersonalBeautyStoreContext => ({
      pbsData,
      storeInfo,
      inviteFriendData,
      canDisplaySponsorInfo,
      canDisplayTooltip,
      tooltipWasShown,
      setTooltipWasShown,
    }),
    [
      pbsData,
      storeInfo,
      inviteFriendData,
      canDisplaySponsorInfo,
      canDisplayTooltip,
      tooltipWasShown,
      setTooltipWasShown,
    ],
  );

  return <personalBeautyStoreContext.Provider value={value}>{children}</personalBeautyStoreContext.Provider>;
};
